"use client";

import { api } from "@agroproj/backend/convex/_generated/api";
import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import { useConvex, useQuery } from "convex/react";
import { Calendar, Copy, ExternalLink, RefreshCw, XCircle } from "lucide-react";
import { parseAsArrayOf, parseAsString, useQueryStates } from "nuqs";
import { useMemo, useState, useTransition } from "react";
import { toast } from "sonner";

import { PageEyebrow } from "@/components/public/page-eyebrow";
import {
  PUBLIC_CONTAINER,
  PUBLIC_PANEL_RADIUS,
  PUBLIC_SECTION_PY_CONTENT,
} from "@/components/public/public-layout-styles";
import { AppImage } from "@/components/ui/app-image";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { dateTimeFormat, longDateFormat, relativeTimeFormat } from "@/lib/date-format";
import { cn } from "@/lib/utils";

const SAO_PAULO_TZ = "America/Sao_Paulo";

interface NewsArticle {
  _id: Id<"sector_news_articles">;
  source: string;
  sourceName: string;
  title: string;
  excerpt?: string;
  imageUrl?: string;
  publishedAt: number;
  originalUrl: string;
}

interface ArticlesPage {
  page: NewsArticle[];
  continueCursor: Id<"sector_news_articles"> | null;
  isDone: boolean;
  totalMatched: number;
  sourceCounts: Array<{ key: string; count: number }>;
}

interface IngestionStatus {
  lastIngestedAt: number | null;
  articleCount: number;
}

interface NoticiasContentProps {
  initialPage: ArticlesPage;
  ingestionStatus?: IngestionStatus;
  sources: { key: string; label: string; url?: string }[];
  pageSize: number;
}

function calendarDay(timestamp: number): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: SAO_PAULO_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(timestamp));
}

function shiftIsoDate(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    return isoDate;
  }
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return shifted.toISOString().slice(0, 10);
}

function formatClockTime(timestamp: number): string {
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: SAO_PAULO_TZ,
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

function formatDayHeading(day: string, today: string, yesterday: string): string {
  if (day === today) return "Hoje";
  if (day === yesterday) return "Ontem";
  const [year, month, date] = day.split("-").map(Number);
  if (year === undefined || month === undefined || date === undefined) {
    return day;
  }
  return longDateFormat(new Date(year, month - 1, date));
}

function groupArticlesByDay(articles: NewsArticle[]) {
  const groups: Array<{ day: string; articles: NewsArticle[] }> = [];
  for (const article of articles) {
    const day = calendarDay(article.publishedAt);
    const last = groups[groups.length - 1];
    if (last && last.day === day) {
      last.articles.push(article);
    } else {
      groups.push({ day, articles: [article] });
    }
  }
  return groups;
}

function ArticleThumbnail({ href, src }: { href: string; src?: string }) {
  const [failed, setFailed] = useState(false);
  // Some feeds still publish http image URLs; the page is https-only.
  const secureSrc = src?.replace(/^http:\/\//i, "https://");
  const showImage = Boolean(secureSrc) && !failed;

  return (
    <a
      className="border-border/40 bg-muted mt-0.5 block h-16 w-24 shrink-0 overflow-hidden rounded-md border md:h-20 md:w-28"
      href={href}
      rel="noopener noreferrer"
      tabIndex={-1}
      target="_blank"
    >
      {showImage && secureSrc ? (
        <AppImage
          alt=""
          className="size-full object-cover"
          height={80}
          loading="lazy"
          onError={() => setFailed(true)}
          sizes="(min-width: 768px) 112px, 96px"
          src={secureSrc}
          width={112}
        />
      ) : null}
    </a>
  );
}

export function NoticiasContent({
  initialPage,
  ingestionStatus,
  sources,
  pageSize,
}: NoticiasContentProps) {
  const [currentTime] = useState(() => Date.now());
  const convex = useConvex();
  const allSourceKeys = useMemo(() => sources.map((source) => source.key), [sources]);

  const [urlParams, setUrlParams] = useQueryStates(
    {
      search: parseAsString.withDefault(""),
      sources: parseAsArrayOf(parseAsString),
    },
    { history: "replace" },
  );

  const searchTerm = urlParams.search;
  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  const urlSelectedSources = urlParams.sources;
  const selectedSources = useMemo(
    () => (urlSelectedSources !== null ? urlSelectedSources : allSourceKeys),
    [urlSelectedSources, allSourceKeys],
  );
  const allSourcesSelected =
    selectedSources.length === allSourceKeys.length &&
    allSourceKeys.every((key) => selectedSources.includes(key));
  const urlHasFilters = Boolean(searchTerm.trim()) || urlSelectedSources !== null;
  const filterKey = `${debouncedSearch.trim().toLowerCase()}::${[...selectedSources].toSorted().join(",")}`;

  const queryArgs = {
    search: debouncedSearch.trim() ? debouncedSearch.trim() : undefined,
    sources: allSourcesSelected ? undefined : selectedSources,
    limit: pageSize,
  };

  const liveFirstPage = useQuery(api.news.publicQueries.listArticlesPage, queryArgs);
  const firstPage = liveFirstPage ?? (urlHasFilters ? undefined : initialPage);

  const [extra, setExtra] = useState<{
    filterKey: string;
    articles: NewsArticle[];
    cursor: Id<"sector_news_articles"> | null;
    isDone: boolean;
  }>({ filterKey: "", articles: [], cursor: null, isDone: true });
  const [isPending, startTransition] = useTransition();

  const extraForFilter = extra.filterKey === filterKey ? extra : null;
  const articles = [...(firstPage?.page ?? []), ...(extraForFilter?.articles ?? [])];
  const continueCursor =
    extraForFilter && extraForFilter.articles.length > 0
      ? extraForFilter.cursor
      : (firstPage?.continueCursor ?? null);
  const isDone =
    extraForFilter && extraForFilter.articles.length > 0
      ? extraForFilter.isDone
      : (firstPage?.isDone ?? true);
  const totalMatched = firstPage?.totalMatched ?? 0;

  const setSearchTerm = (value: string) => {
    setUrlParams({ search: value ? value : null });
  };

  const setSelectedSources = (value: string[]) => {
    const isAll =
      value.length === allSourceKeys.length && allSourceKeys.every((key) => value.includes(key));
    setUrlParams({ sources: isAll ? null : value });
  };

  const toggleSource = (key: string) => {
    setSelectedSources(
      selectedSources.includes(key)
        ? selectedSources.filter((source) => source !== key)
        : [...selectedSources, key],
    );
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copiado! Compartilhe a curadoria com seus filtros.");
    } catch {
      toast.error("Não foi possível copiar o link automaticamente.");
    }
  };

  const handleLoadMore = () => {
    if (!continueCursor || isDone) return;

    startTransition(async () => {
      const nextPage = await convex.query(api.news.publicQueries.listArticlesPage, {
        ...queryArgs,
        cursor: continueCursor,
      });
      setExtra((current) => {
        const previous = current.filterKey === filterKey ? current.articles : [];
        return {
          filterKey,
          articles: [...previous, ...nextPage.page],
          cursor: nextPage.continueCursor,
          isDone: nextPage.isDone,
        };
      });
    });
  };

  const sourcesText = sources.map((source) => source.label).join(", ");
  const catalogArticleCount = ingestionStatus?.articleCount ?? 0;
  const today = calendarDay(currentTime);
  const yesterday = shiftIsoDate(today, -1);
  const dayGroups = groupArticlesByDay(articles);

  const feedCounts = useMemo(() => {
    const counts = Object.fromEntries(allSourceKeys.map((key) => [key, 0]));
    for (const entry of firstPage?.sourceCounts ?? []) {
      counts[entry.key] = entry.count;
    }
    return counts;
  }, [allSourceKeys, firstPage?.sourceCounts]);

  const filterSources = useMemo(() => {
    if (debouncedSearch.trim()) return sources;
    return sources.filter((source) => (feedCounts[source.key] ?? 0) > 0);
  }, [debouncedSearch, feedCounts, sources]);

  const formatRelativeTime = (timestamp: number | null | undefined): string => {
    if (!timestamp) return "nunca";
    return relativeTimeFormat(timestamp);
  };

  const isInitialEmpty = catalogArticleCount === 0 && !urlHasFilters && articles.length === 0;
  const isFilterEmpty = firstPage !== undefined && totalMatched === 0 && !isInitialEmpty;

  return (
    <main className="relative z-10 pb-16">
      <div className={cn(PUBLIC_CONTAINER, PUBLIC_SECTION_PY_CONTENT, "pb-16")}>
        <div className="mb-10">
          <PageEyebrow className="mb-3" icon={RefreshCw}>
            Curadoria diária
          </PageEyebrow>

          <h1 className="mb-3 max-w-[30ch] text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
            Notícias do setor agropecuário
          </h1>
          <p className="text-muted-foreground max-w-[48ch] text-lg text-pretty">
            Manchetes e trechos curtos para quem estrutura crédito e investimento rural. A leitura
            completa fica no site de origem.
          </p>

          <div className="text-muted-foreground mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Calendar aria-hidden="true" className="size-4" />
              <span>
                Atualizado{" "}
                {ingestionStatus?.lastIngestedAt ? (
                  <time
                    dateTime={new Date(ingestionStatus.lastIngestedAt).toISOString()}
                    title={dateTimeFormat(ingestionStatus.lastIngestedAt)}
                  >
                    {formatRelativeTime(ingestionStatus.lastIngestedAt)}
                  </time>
                ) : (
                  "aguardando a primeira sincronização"
                )}
              </span>
            </div>
            {firstPage && totalMatched > 0 ? (
              <>
                <span aria-hidden="true">·</span>
                <span>
                  {articles.length < totalMatched
                    ? `${articles.length} de ${totalMatched}`
                    : `${totalMatched} ${totalMatched === 1 ? "notícia" : "notícias"}`}
                </span>
              </>
            ) : null}
          </div>
        </div>

        <div className="bg-background sticky top-16 z-10 mb-8 space-y-3 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative min-w-0 flex-1">
              <Input
                aria-label="Buscar notícias por título, palavra-chave ou fonte"
                autoComplete="off"
                // ui-audit-ignore-next-line forms/no-field-size-override: matches the size="sm" filter buttons in this row
                className="h-8 pr-9"
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar por título, palavra-chave ou fonte…"
                spellCheck={false}
                type="search"
                value={searchTerm}
              />
              {searchTerm ? (
                <button
                  aria-label="Limpar busca"
                  className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute top-1/2 right-2 -translate-y-1/2 rounded-full p-1 focus:outline-none focus-visible:ring-1"
                  onClick={() => setSearchTerm("")}
                  type="button"
                >
                  <XCircle aria-hidden="true" className="size-4" />
                </button>
              ) : null}
            </div>
            <Button
              aria-label="Copiar link com os filtros e busca atuais"
              className="shrink-0"
              onClick={handleCopyLink}
              size="sm"
              variant="outline"
            >
              <Copy aria-hidden="true" className="size-3.5" />
              Copiar link
            </Button>
          </div>

          <div
            aria-label="Filtrar por fontes de notícias"
            aria-orientation="horizontal"
            className="flex scrollbar-none gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] sm:flex-wrap sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden"
            role="toolbar"
          >
            <Button
              aria-label="Selecionar todas as fontes"
              className="shrink-0 text-xs whitespace-nowrap"
              onClick={() => setSelectedSources(allSourceKeys)}
              size="sm"
              variant="outline"
            >
              Todas
            </Button>
            {filterSources.map((source) => {
              const isSelected = selectedSources.includes(source.key);
              const count = feedCounts[source.key] ?? 0;
              const isZero = count === 0;

              return (
                <Button
                  aria-label={`${isSelected ? "Remover" : "Adicionar"} filtro ${source.label}${isZero ? " (sem resultados)" : ""}`}
                  aria-pressed={isSelected}
                  className={cn("shrink-0 text-xs whitespace-nowrap", isZero && "opacity-60")}
                  key={source.key}
                  onClick={() => toggleSource(source.key)}
                  size="sm"
                  variant={isSelected ? "secondary" : "outline"}
                >
                  {source.label} ({count})
                </Button>
              );
            })}
          </div>
        </div>

        {isInitialEmpty ? (
          <div className={cn("border p-6 text-center", PUBLIC_PANEL_RADIUS)}>
            <div className="bg-muted mx-auto mb-4 flex size-12 items-center justify-center rounded-full">
              <RefreshCw className="text-muted-foreground size-6" />
            </div>
            <h3 className="mb-2 text-lg font-semibold">Primeira sincronização em andamento</h3>
            <p className="text-muted-foreground mx-auto max-w-md text-sm">
              Estamos configurando a ingestão diária de notícias de fontes confiáveis do agronegócio
              brasileiro. As primeiras notícias devem aparecer em breve.
            </p>
            {ingestionStatus ? (
              <div className="text-muted-foreground mt-4 text-xs">
                Última tentativa de sincronização:{" "}
                {ingestionStatus.lastIngestedAt
                  ? formatRelativeTime(ingestionStatus.lastIngestedAt)
                  : "nunca"}
              </div>
            ) : null}
            <div className="text-muted-foreground mt-6 text-xs">Fontes: {sourcesText}</div>
          </div>
        ) : isFilterEmpty ? (
          <div className={cn("border p-6 text-center", PUBLIC_PANEL_RADIUS)}>
            <p className="text-muted-foreground">
              Nenhuma notícia encontrada com os filtros atuais.
            </p>
            <Button
              className="mt-4"
              onClick={() => {
                setSearchTerm("");
                setSelectedSources(allSourceKeys);
              }}
              variant="outline"
            >
              Limpar filtros
            </Button>
          </div>
        ) : firstPage === undefined ? (
          <div className={cn("border p-6 text-center", PUBLIC_PANEL_RADIUS)}>
            <p className="text-muted-foreground">Carregando notícias…</p>
          </div>
        ) : (
          <div>
            {dayGroups.map((group) => (
              <section className="mb-8" key={group.day}>
                <h2 className="text-muted-foreground mb-3 text-sm font-medium tracking-wide uppercase">
                  {formatDayHeading(group.day, today, yesterday)}
                </h2>
                <div
                  className={cn(
                    "border-border/50 divide-border/50 divide-y border",
                    PUBLIC_PANEL_RADIUS,
                  )}
                >
                  {group.articles.map((article) => {
                    const publishedDate = new Date(article.publishedAt);
                    const dateLabel = formatClockTime(article.publishedAt);
                    const excerpt =
                      article.excerpt &&
                      !/\[object Object\]/i.test(article.excerpt) &&
                      article.excerpt.trim().length > 10
                        ? article.excerpt
                        : null;

                    return (
                      <article className="p-5 sm:p-6" key={article._id}>
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <Badge variant="outline">{article.sourceName}</Badge>
                          <time
                            className="text-muted-foreground text-sm tabular-nums"
                            dateTime={publishedDate.toISOString()}
                            title={dateTimeFormat(publishedDate)}
                          >
                            {dateLabel}
                          </time>
                        </div>

                        <div className="flex gap-3 sm:gap-4">
                          <ArticleThumbnail href={article.originalUrl} src={article.imageUrl} />
                          <div className="min-w-0 flex-1">
                            <h3 className="mb-2 text-xl font-semibold tracking-tight">
                              <a
                                className="focus-visible:ring-ring rounded-sm underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none"
                                href={article.originalUrl}
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                {article.title}
                                <ExternalLink
                                  aria-hidden="true"
                                  className="text-muted-foreground ml-1.5 inline size-3.5 align-[-2px]"
                                />
                                <span className="sr-only"> (abre no site original)</span>
                              </a>
                            </h3>
                            {excerpt ? (
                              <p className="text-muted-foreground line-clamp-3 text-base text-pretty">
                                {excerpt}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}

            {!isDone && continueCursor ? (
              <div className="flex justify-center">
                <Button disabled={isPending} onClick={handleLoadMore} size="sm" variant="outline">
                  {isPending ? "Carregando…" : "Carregar mais"}
                </Button>
              </div>
            ) : null}
          </div>
        )}

        <div className="text-muted-foreground mt-12 border-t pt-8 text-sm">
          <p className="text-foreground mb-4 font-medium">Fontes utilizadas nesta curadoria</p>
          <ul className="grid gap-x-8 gap-y-1 sm:grid-cols-2 md:grid-cols-3">
            {sources.map((source) => {
              const articleCount = feedCounts[source.key] ?? 0;
              return (
                <li key={source.key}>
                  {source.url ? (
                    <a
                      className="hover:text-foreground underline-offset-2 transition-colors hover:underline"
                      href={source.url}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {source.label}
                    </a>
                  ) : (
                    source.label
                  )}
                  <span className="text-muted-foreground ml-1 tabular-nums">({articleCount})</span>
                </li>
              );
            })}
          </ul>

          <div className={cn("bg-surface mt-6 border p-4 text-sm/relaxed", PUBLIC_PANEL_RADIUS)}>
            <strong>Aviso:</strong> Esta página agrega automaticamente manchetes e trechos curtos de
            veículos jornalísticos e instituições oficiais do setor agropecuário brasileiro. O
            conteúdo completo permanece de responsabilidade das fontes originais. Recomendamos
            sempre a leitura integral nos sites de origem. AgroProj não endossa opiniões ou garante
            a veracidade das informações publicadas por terceiros.
          </div>
        </div>
      </div>
    </main>
  );
}
